import React, { useState } from 'react';
import { Mail, User, Phone, CheckCircle, Gift, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const ClaimPrizeForm = ({ prize, gameId, prizeId, onClaimSuccess }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isClaimed, setIsClaimed] = useState(false);

    // ⭐ CRITICAL FIX: Ensure blank spaces aren't counted as winning codes
    const isWinner = !!(prize.code && prize.code.trim());

    if (isClaimed) {
        return (
            <div className="p-6 text-center bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl">
                <CheckCircle className="mx-auto w-12 h-12 text-green-600 mb-3" />
                <h3 className="text-xl font-bold text-green-800 dark:text-green-300">
                    {isWinner ? 'Success! Your Prize is Reserved.' : 'Thanks for playing!'}
                </h3>
                <p className="mt-2 text-sm text-green-700 dark:text-green-400">
                    {isWinner ? (
                        <>We've sent the details for your <span className="font-bold">{prize.title}</span> to your email. We'll be in touch shortly!</>
                    ) : (
                        "Your details have been saved safely. Keep an eye on your inbox for future exclusive offers and games!"
                    )}
                </p>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        if (!name || !email || !phone) {
            toast.error("Please fill in all required fields.");
            setIsSubmitting(false);
            return;
        }

        try {
            const res = await api.post('/public/scratch-card/claim', {
                gameId,
                prizeId: prizeId,
                prizeCode: prize.code,
                name,
                email,
                phone,
            });

            toast.success(res.data.message || 'Successfully submitted!');
            setIsClaimed(true);
            if (onClaimSuccess) onClaimSuccess(res.data.lead);
            
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to submit. Please try again.';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-4">
            <h3 className={`text-2xl font-bold text-center ${isWinner ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-300'}`}>
                {isWinner ? `You Won: ${prize.title}!` : `Outcome: ${prize.title}`}
            </h3>
            <p className="text-center text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                {isWinner ? 
                    "To reserve your discount and receive your exclusive code, please provide your contact details below." :
                    "Thank you for playing. Provide your details below to join our mailing list for future offers!"
                }
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4 p-4 border border-dashed border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 shadow-inner">
                
                {/* Name */}
                <div className="relative">
                    <User className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    <input 
                        type="text" 
                        placeholder="Full Name" 
                        value={name} 
                        onChange={(e) => setName(e.target.value)} 
                        required
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        disabled={isSubmitting}
                    />
                </div>
                
                {/* Email */}
                <div className="relative">
                    <Mail className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    <input 
                        type="email" 
                        placeholder="Email Address" 
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)} 
                        required
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        disabled={isSubmitting}
                    />
                </div>
                
                {/* Phone */}
                <div className="relative">
                    <Phone className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    <input 
                        type="tel" 
                        placeholder="Phone Number" 
                        value={phone} 
                        onChange={(e) => setPhone(e.target.value)} 
                        required
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        disabled={isSubmitting}
                    />
                </div>

                <button 
                    type="submit" 
                    className="w-full px-4 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...
                        </>
                    ) : isWinner ? (
                         <>
                            <Gift className="w-5 h-5 mr-2" /> Claim Your {prize.title}
                        </>
                    ) : (
                        "Get Future Offers"
                    )}
                </button>
            </form>
        </div>
    );
};

export default ClaimPrizeForm;